import { useEffect, useState } from "react";
import { useT } from "../i18n/LocaleProvider";
import type { Gender } from "../i18n/strings";

interface Props {
  animalName: string;
  /** Gender of `animalName` in the current language — drives "un"/"une". */
  animalGender: Gender;
  /** Optional fun fact shown on finishing (used by Famous Paintings). */
  fact?: string;
  /** Set when this drawing crossed a 5-drawing milestone — the new animal
   *  friend to reveal right away, before offering the next drawing. */
  reward?: { emoji: string; name: string; size: number; article: string };
  /** Draw the freshly-won sticker right away. Set only when the reward maps to a
   *  drawable friend — makes its emoji (and a button) tappable in the card. */
  onDrawReward?: () => void;
  /** Set when the child just cleared a whole level — congratulate and announce
   *  the new level we're moving up to. */
  levelUp?: { from: string; to: string };
  /** Name/emoji of the drawing we'll auto-advance to. Omitted when there's no
   *  other drawing to offer (e.g. the only earned sticker) — then no "Next"
   *  button or countdown is shown. */
  nextName?: string;
  nextEmoji?: string;
  /** Start the next drawing now (also fired automatically when the timer ends).
   *  Omitted when there's nothing to advance to. */
  onAutoNext?: () => void;
  /** Go back to the home screen to pick another drawing. */
  onAgain: () => void;
  /** Replay the same drawing from step 1. */
  onWatchAgain: () => void;
  /** Dismiss the celebration and return to the last step (e.g. opened by mistake). */
  onBack: () => void;
}

const COUNTDOWN = 15;

// Firework bursts at varied positions, colors, and timings.
const BURSTS = [
  { left: "22%", top: "28%", color: "#ef476f", delay: "0s" },
  { left: "72%", top: "22%", color: "#ffd166", delay: "0.45s" },
  { left: "48%", top: "14%", color: "#06d6a0", delay: "0.9s" },
  { left: "82%", top: "52%", color: "#118ab2", delay: "1.15s" },
  { left: "16%", top: "58%", color: "#9b5de5", delay: "0.65s" },
  { left: "58%", top: "46%", color: "#ff7b00", delay: "1.45s" },
];

export function Celebration({
  animalName,
  animalGender,
  fact,
  reward,
  onDrawReward,
  levelUp,
  nextName,
  nextEmoji,
  onAutoNext,
  onAgain,
  onWatchAgain,
  onBack,
}: Props) {
  const t = useT();
  const [secs, setSecs] = useState(COUNTDOWN);

  const hasNext = !!onAutoNext;
  // Auto-advance only when there IS a next drawing and no reward to enjoy first
  // (a new animal friend pauses the countdown so the child can savour it).
  const autoAdvance = hasNext && !reward;

  // Tick down once a second; when it hits zero, auto-advance to the next drawing.
  useEffect(() => {
    if (!autoAdvance) return;
    if (secs <= 0) {
      onAutoNext?.();
      return;
    }
    const t = setTimeout(() => setSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [autoAdvance, secs, onAutoNext]);

  return (
    <div className="celebration" role="dialog" aria-live="assertive">
      <div className="fireworks" aria-hidden="true">
        {BURSTS.map((b, i) => (
          <span
            key={i}
            className="firework"
            style={{
              left: b.left,
              top: b.top,
              color: b.color,
              animationDelay: b.delay,
            }}
          />
        ))}
      </div>

      <div className="celebration-card">
        <button
          className="celebration-close"
          onClick={onBack}
          aria-label={t.goBackToDrawing}
        >
          ✕
        </button>
        <div className="celebration-emoji" aria-hidden="true">
          {reward ? "🎁✨🎉" : levelUp ? "🏆🎉🚀" : "🎉🌟🎨"}
        </div>
        {levelUp ? (
          <>
            <h2>{t.levelUpTitle(levelUp.from)}</h2>
            <p>
              {t.youDrew(animalName, animalGender)}
              <strong>{animalName}</strong>
              {t.levelUpAllDone(levelUp.from)}
            </p>
            <p className="celebration-levelup">{t.levelUpReady(levelUp.to)}</p>
          </>
        ) : (
          <>
            <h2>{t.youDidIt}</h2>
            <p>
              {t.youDrew(animalName, animalGender)}
              <strong>{animalName}</strong>
              {t.youDrewSuffix}
            </p>
          </>
        )}
        {reward && (
          <div className="celebration-reward" aria-live="polite">
            <p className="celebration-reward-head">{t.newFriend}</p>
            {/* The friend's emoji doubles as a big, tappable "draw it" target
                when the reward maps to a drawable friend. */}
            {onDrawReward ? (
              <button
                className="celebration-reward-stage celebration-reward-tap"
                onClick={onDrawReward}
                aria-label={t.drawYourReward(reward.article, reward.name)}
                title={t.drawYourReward(reward.article, reward.name)}
              >
                <span className="reward-sparkle s1" aria-hidden="true">✨</span>
                <span className="reward-sparkle s2" aria-hidden="true">⭐</span>
                <span className="reward-sparkle s3" aria-hidden="true">✨</span>
                <span className="reward-sparkle s4" aria-hidden="true">🌟</span>
                <span
                  className="celebration-reward-emoji"
                  style={{ fontSize: `${Math.max(reward.size, 3)}rem` }}
                  aria-hidden="true"
                >
                  {reward.emoji}
                </span>
              </button>
            ) : (
              <span className="celebration-reward-stage" aria-hidden="true">
                <span className="reward-sparkle s1">✨</span>
                <span className="reward-sparkle s2">⭐</span>
                <span className="reward-sparkle s3">✨</span>
                <span className="reward-sparkle s4">🌟</span>
                <span
                  className="celebration-reward-emoji"
                  style={{ fontSize: `${Math.max(reward.size, 3)}rem` }}
                >
                  {reward.emoji}
                </span>
              </span>
            )}
            <p className="celebration-reward-name">
              {t.youEarned(reward.article)}
              <strong>{reward.name}</strong>
              {t.youEarnedSuffix}
            </p>
            {onDrawReward && (
              <button className="home-btn big reward-draw-btn" onClick={onDrawReward}>
                {t.drawYourReward(reward.article, reward.name)}
              </button>
            )}
          </div>
        )}

        {fact && <p className="celebration-fact">💡 {fact}</p>}

        <div className="celebration-buttons">
          {hasNext && (
            <button className="home-btn big next-btn" onClick={onAutoNext}>
              <span>
                {t.nextLabel} {nextEmoji} {nextName}
              </span>
              {autoAdvance && (
                <span className="next-count" aria-live="polite">
                  {t.startingIn(secs)}
                </span>
              )}
            </button>
          )}
          <button
            className={hasNext ? "ghost-btn" : "home-btn big next-btn"}
            onClick={onAgain}
          >
            {t.pickAnother}
          </button>
          <button className="ghost-btn" onClick={onWatchAgain}>
            {t.watchAgain}
          </button>
        </div>
      </div>
    </div>
  );
}
